import React, { useEffect, useState } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { Alert, Button, List, message, Modal, Space, Upload, Typography, type ModalProps } from "antd";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import { useApiUrl } from "@refinedev/core";
import { getUserInfoFromCache } from "utility/helper";
import styled from "@emotion/styled";

const { Text } = Typography;

const SelectFileContainer = styled("div")`
  display: flex;
  gap: 0.5rem;
`;

const SkippedSpecsListWrap = styled("div")`
  max-height: 280px;
  overflow: auto;
  margin-top: 8px;

  .ant-list-item {
    border: none !important;
    border-bottom: 1px solid #8c8c8c !important;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .ant-list-item:last-child {
    border-bottom: none !important;
  }
`;

const FILE_TYPE = "ProductSpecificationsCsv";

type ImportProductSpecificationsModalProps = {
  modalProps: ModalProps;
  onFinish?: () => void;
};

type SkippedSpecification = {
  mainItemNo?: string;
  label?: string;
  value?: string;
  reason?: string;
};

type ImportResponse = {
  message?: string;
  jobId?: number;
  summary?: string;
  productsProcessed?: number;
  specificationsUpserted?: number;
  productsSkippedNotFound?: number;
  specificationsSkippedUnknownUnit?: number;
  rowsSkippedInvalid?: number;
  skippedMainItemNos?: string[];
  skippedSpecifications?: SkippedSpecification[];
  error?: string;
};

const ImportProductSpecificationsModal: React.FC<ImportProductSpecificationsModalProps> = ({
  modalProps,
  onFinish,
}) => {
  const [file, setFile] = useState<UploadFile | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [blobArray, setBlobArray] = useState<Blob[]>([]);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const apiUrl = useApiUrl();

  useEffect(() => {
    if (modalProps.open === false) {
      setImportResult(null);
      setFile(undefined);
      setBlobArray([]);
    }
  }, [modalProps.open]);

  const applyImportResult = (data: ImportResponse) => {
    setImportResult(data);
    setFile(undefined);

    const processed = data.productsProcessed ?? 0;
    const upserted = data.specificationsUpserted ?? 0;
    const missingProducts = data.productsSkippedNotFound ?? 0;
    const skippedSpecs = data.specificationsSkippedUnknownUnit ?? 0;

    if (upserted === 0 && (missingProducts > 0 || skippedSpecs > 0)) {
      message.warning("Import klar, men inga specifikationer sparades. Se sammanfattningen nedan.");
      return;
    }

    message.success(
      `Import klar. Produkter: ${processed}, specifikationer: ${upserted}` +
        (skippedSpecs > 0 ? `, hoppade över: ${skippedSpecs}` : "") +
        (missingProducts > 0 ? `, saknade produkter: ${missingProducts}` : ""),
    );
  };

  const handleUpload = async () => {
    const formData = new FormData();
    formData.append("file", file as RcFile);
    setUploading(true);
    const userInfo = getUserInfoFromCache();

    try {
      const res = await fetch(`${apiUrl}/import/file-upload?filetype=${FILE_TYPE}`, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      const data = (await res.json().catch(() => ({}))) as ImportResponse;
      if (!res.ok) {
        throw new Error(data.error || data.message || "Upload failed");
      }
      applyImportResult(data);
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Uppladdningen misslyckades.");
    } finally {
      setUploading(false);
    }
  };

  const chunkUpload = async () => {
    setUploading(true);
    message.info("Filen är för stor. Påbörjar uppladdning i delar.");
    const userInfo = getUserInfoFromCache();

    try {
      for (let i = 0; i < blobArray.length; i++) {
        const formData = new FormData();
        formData.append("chunkIndex", i.toString());
        formData.append("totalChunks", blobArray.length.toString());
        formData.append("file", blobArray[i] as RcFile);

        const res = await fetch(`${apiUrl}/import/chunk-file-upload?filetype=${FILE_TYPE}`, {
          method: "POST",
          body: formData,
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        const data = (await res.json().catch(() => ({}))) as ImportResponse;

        if (!res.ok) {
          throw new Error(data.error || data.message || "Chunk upload failed");
        }

        if (i === blobArray.length - 1) {
          applyImportResult(data);
        } else {
          message.success(`Laddar upp del ${i + 1} av ${blobArray.length}.`);
        }
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Uppladdningen misslyckades.");
    } finally {
      setFile(undefined);
      setBlobArray([]);
      setUploading(false);
    }
  };

  const props: UploadProps = {
    onRemove: () => {
      setFile(undefined);
      setBlobArray([]);
    },
    beforeUpload: (selectedFile) => {
      const isLt5M = selectedFile.size / 1024 / 1024 < 5;

      if (!isLt5M) {
        const chunkSize = 1024 * 1024 * 7;
        const chunks = Math.ceil(selectedFile.size / chunkSize);
        const chunkArray = [];
        for (let i = 0; i < chunks; i++) {
          const start = i * chunkSize;
          const end = Math.min(start + chunkSize, selectedFile.size);
          chunkArray.push(selectedFile.slice(start, end));
        }
        setBlobArray(chunkArray);
      } else {
        setBlobArray([]);
      }

      setFile(selectedFile);
      setImportResult(null);
      return false;
    },
    maxCount: 1,
    accept: ".csv",
    fileList: file ? [file] : [],
  };

  const skippedSpecs = importResult?.skippedSpecifications ?? [];
  const skippedProducts = importResult?.skippedMainItemNos ?? [];

  return (
    <Modal
      {...modalProps}
      width={720}
      title="Importera specifikationer"
      onOk={onFinish}
      footer={
        importResult ? (
          <Button type="primary" onClick={onFinish}>
            Stäng
          </Button>
        ) : null
      }
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {!importResult && (
          <>
            <Text type="secondary">
              Ladda upp en CSV där första kolumnen är <Text code>artikelnummer - titel</Text> och
              övriga kolumnrubriker är svenska specifikationsetiketter. Värden ska vara{" "}
              <Text code>värde enhet</Text> (t.ex. <Text code>6.1 Ah</Text>,{" "}
              <Text code>430 mm</Text>). Enheten måste finnas i systemet — annars hoppas cellen
              över. Tomma celler och saknade produkter hoppas också över.
            </Text>
            <SelectFileContainer>
              <Upload {...props}>
                <Button icon={<UploadOutlined />}>Välj CSV-fil</Button>
              </Upload>
            </SelectFileContainer>
            <Button
              type="primary"
              onClick={blobArray?.length > 0 ? chunkUpload : handleUpload}
              disabled={!file}
              loading={uploading}
            >
              {uploading ? "Importerar..." : "Starta import"}
            </Button>
          </>
        )}

        {importResult && (
          <>
            <Alert
              type={(importResult.specificationsUpserted ?? 0) > 0 ? "success" : "warning"}
              showIcon
              message="Importresultat"
              description={
                <div>
                  <div>Produkter uppdaterade: {importResult.productsProcessed ?? 0}</div>
                  <div>Specifikationer sparade: {importResult.specificationsUpserted ?? 0}</div>
                  <div>Saknade produkter: {importResult.productsSkippedNotFound ?? 0}</div>
                  <div>
                    Specifikationer hoppade över:{" "}
                    {importResult.specificationsSkippedUnknownUnit ?? 0}
                  </div>
                </div>
              }
            />

            {skippedProducts.length > 0 && (
              <div>
                <Text strong>Saknade artikelnummer ({skippedProducts.length})</Text>
                <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
                  {skippedProducts.join(", ")}
                </Text>
              </div>
            )}

            {skippedSpecs.length > 0 && (
              <div>
                <Text strong>
                  Specifikationer som inte importerades ({skippedSpecs.length})
                </Text>
                <SkippedSpecsListWrap>
                  <List
                    size="small"
                    bordered
                    split={false}
                    dataSource={skippedSpecs}
                    renderItem={(item) => (
                      <List.Item>
                        <Space direction="vertical" size={0} style={{ width: "100%" }}>
                          <Text>
                            <Text code>{item.mainItemNo}</Text> — {item.label}:{" "}
                            <Text code>{item.value}</Text>
                          </Text>
                          <Text type="secondary">{item.reason}</Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                </SkippedSpecsListWrap>
              </div>
            )}
          </>
        )}
      </Space>
    </Modal>
  );
};

export default ImportProductSpecificationsModal;
